import { Component, inject, input, OnChanges, OnInit } from '@angular/core';
import { ButtonStyles } from '../../../../button/button.component';
import { ZiroFormConfig } from '../../../../forms/ziro-forms-models';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import {
  CiscoDialPlanFieldConfig,
  DialPlanGroupEntry,
  DialPlanGroupEntryType,
  UsageType,
} from '../../../../shared/models/generated/ziroModels';
import { Observable, of } from 'rxjs';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import {
  ExtensionSelectorSize,
  ZiroExtensionSelectorConfig,
} from '../../../shared/extension-selector/extension-selector.component';
import { DialPlanInventoriesResource } from '../../../../shared/resources/dial-plan-inventories.resource';
import { map } from 'rxjs/operators';

export interface DnDidSelectorForm {
  dialPlanGroupId: number;
  extension: string;
  pattern: string;
}

export interface DnDidSelectorFormEntity {
  dialPlanGroupId: number;
  directoryNumber: {
    extension: string;
  };
  translationPattern: {
    pattern: string;
  };
}

@Component({
  selector: 'ziro-directory-number-dn-did-selector-form',
  templateUrl: './directory-number-dn-did-selector-form.component.html',
  styleUrls: ['./directory-number-dn-did-selector-form.component.scss'],
  standalone: false,
})
export class ZiroDirectoryNumberDnDidSelectorFormComponent
  extends ZiroFormAbstractDirective<DnDidSelectorFormEntity, DnDidSelectorForm>
  implements OnInit, OnChanges
{
  protected _ziroFormStateService: ZiroFormStateService;
  private dialPlanInventoriesResource = inject(DialPlanInventoriesResource);

  readonly dialPlanGroups = input<CiscoDialPlanFieldConfig[]>([]);
  readonly initialDialPlanGroupId = input<number>();
  readonly initialExtension = input<string>();
  readonly isZpmSyncWarningPresent = input<boolean>();

  buttonStyles = ButtonStyles;
  ziroIcons = ZiroIcons;

  showInactive = true;
  showRecentlyDeleted = true;
  showUnavailable = false;

  formConfig: ZiroFormConfig;

  selectedDialPlanGroup: CiscoDialPlanFieldConfig;
  selectedGroupDnOptions: DialPlanGroupEntry[] = [];
  selectedGroupDidOptions: DialPlanGroupEntry[] = [];
  selectorSize = ExtensionSelectorSize.SMALL;

  isSubmitted = false;
  isSearching = false;

  ngOnInit(): void {
    this._initFormConfig();

    if (this.initialDialPlanGroupId()) {
      this.selectedDialPlanGroup = this.dialPlanGroups().find(
        (dialPlanGroup) => dialPlanGroup.id === this.initialDialPlanGroupId(),
      );
      if (this.selectedDialPlanGroup) {
        this.onDialPlanGroupChange(false);
      }
    }

    this._validateAndSubmitSource.subscribe(() => {
      this.isSubmitted = true;
    });
  }

  toFormData = (entity: DnDidSelectorFormEntity): DnDidSelectorForm => {
    return {
      dialPlanGroupId: entity?.dialPlanGroupId,
      extension: entity?.directoryNumber?.extension,
      pattern: entity?.translationPattern?.pattern,
    };
  };

  toEntity = (formData: DnDidSelectorForm): DnDidSelectorFormEntity => {
    return {
      dialPlanGroupId: this.selectedDialPlanGroup?.id,
      directoryNumber: {
        extension: formData.extension,
      },
      translationPattern: {
        pattern: formData.pattern,
      },
    };
  };

  isFormValid(): boolean {
    return !!this.selectedDialPlanGroup;
  }

  revert(oldValue: DnDidSelectorFormEntity): void {
    this.entitySource.next(oldValue);
  }

  onDialPlanGroupChange(setFormDirty = true): void {
    this.isSearching = true;
    this.entitySource.next({
      ...this.entity,
      dialPlanGroupId: this.selectedDialPlanGroup ? this.selectedDialPlanGroup.id : null,
    });
    this.applyComponentConfig();
    this._ziroFormStateService.setIsFormDirty(setFormDirty);
  }

  applyConfigs(): void {
    this.selectorSize = this.selectedGroupDidOptions.length ? ExtensionSelectorSize.SMALL : ExtensionSelectorSize.LARGE;

    const dnComponent = this.fieldComponents.find((field) => field.fieldId() === 'extension');
    dnComponent.applyComponentConfig(
      new ZiroExtensionSelectorConfig({
        options: this.selectedGroupDnOptions,
        showRecentlyDeleted: this.showRecentlyDeleted,
        showUnavailable: this.showUnavailable,
        size: this.selectorSize,
        showInactive: this.showInactive,
      }),
    );

    const didComponent = this.fieldComponents.find((field) => field.fieldId() === 'pattern');
    didComponent.applyComponentConfig(
      new ZiroExtensionSelectorConfig({
        options: this.selectedGroupDidOptions,
        showRecentlyDeleted: this.showRecentlyDeleted,
        showUnavailable: this.showUnavailable,
        size: this.selectorSize,
        showInactive: this.showInactive,
      }),
    );
  }

  applyComponentConfig(): void {
    this._getDialPlanInventory(this.selectedDialPlanGroup.id).subscribe(() => {
      this.applyConfigs();
      this.isSearching = false;
    });
  }

  protected submit(): Observable<null> {
    return of(null);
  }

  private _initFormConfig(): void {
    this.formConfig = {
      fields: {
        dialPlanGroupId: {
          label: '',
          dataAutomation: 'dn-did-selector-group',
          hidden: () => true,
        },
        extension: {
          label: 'tkey;dndidselection.dn',
          dataAutomation: 'dn-extension-selector',
          required: true,
          componentConfig: new ZiroExtensionSelectorConfig({
            options: this.selectedGroupDnOptions,
            showRecentlyDeleted: this.showRecentlyDeleted,
            showUnavailable: this.showUnavailable,
            size: this.selectorSize,
            showInactive: this.showInactive,
          }),
        },
        pattern: {
          label: 'tkey;dndidselection.did',
          dataAutomation: 'did-extension-selector',
          required: false,
          componentConfig: new ZiroExtensionSelectorConfig({
            options: this.selectedGroupDidOptions,
            showRecentlyDeleted: this.showRecentlyDeleted,
            showUnavailable: this.showUnavailable,
            size: this.selectorSize,
            showInactive: this.showInactive,
          }),
        },
      },
    };
  }

  private _getDialPlanInventory(groupId: number): Observable<void> {
    return this.dialPlanInventoriesResource.get(groupId).pipe(
      map((inventory) => {
        this.selectedGroupDnOptions = inventory.dialPlanEntries;

        this.selectedGroupDidOptions = inventory.translationPatternEntries.map<DialPlanGroupEntry>((tpEntry) => {
          return {
            ...tpEntry,
            pbxType: DialPlanGroupEntryType.CUCM,
            usageTypes: [UsageType.NOT_APPLICABLE],
            usageLocation: '',
          };
        });
      }),
    );
  }
}
